interface SkeletonProps {
  readonly width?: string;
  readonly height?: string;
  readonly borderRadius?: string;
  readonly className?: string;
}

const Skeleton = ({
  width = '100%',
  height = '1rem',
  borderRadius = 'var(--radius-md)',
  className = '',
}: SkeletonProps) => (
  <div
    className={`animate-pulse ${className}`}
    style={{
      width,
      height,
      borderRadius,
      backgroundColor: 'var(--bg-surface-sunken)',
    }}
  />
);

/** Skeleton that mimics a product card in the catalog grid. */
export const ProductCardSkeleton = () => (
  <div
    style={{
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--border-default)',
      backgroundColor: 'var(--bg-surface)',
      padding: 'var(--space-4)',
    }}
  >
    <Skeleton height="12rem" borderRadius="var(--radius-md)" />
    <Skeleton width="60%" height="1.25rem" className="mt-4" />
    <Skeleton width="80%" height="0.875rem" className="mt-2" />
    <div className="flex items-center justify-between mt-3">
      <Skeleton width="4rem" height="1.25rem" />
      <Skeleton width="5rem" height="1.5rem" borderRadius="var(--radius-full)" />
    </div>
  </div>
);

/** Skeleton that mimics a list row (quotes, orders). */
export const ListRowSkeleton = () => (
  <div
    style={{
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--border-default)',
      backgroundColor: 'var(--bg-surface)',
      padding: 'var(--space-4)',
    }}
  >
    <div className="flex items-center justify-between">
      <div style={{ flex: 1 }}>
        <Skeleton width="40%" height="1rem" />
        <Skeleton width="25%" height="0.75rem" className="mt-2" />
      </div>
      <div className="flex items-center" style={{ gap: 'var(--space-3)' }}>
        <Skeleton width="4rem" height="0.875rem" />
        <Skeleton width="5rem" height="1.5rem" borderRadius="var(--radius-full)" />
      </div>
    </div>
  </div>
);

/** Skeleton grid for the product catalog. */
export const ProductGridSkeleton = ({ count = 8 }: { readonly count?: number }) => (
  <div
    className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    style={{ gap: 'var(--space-6)' }}
  >
    {Array.from({ length: count }, (_, i) => (
      <ProductCardSkeleton key={i} />
    ))}
  </div>
);

/** Skeleton list for quotes or orders. */
export const ListSkeleton = ({ count = 5 }: { readonly count?: number }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
    {Array.from({ length: count }, (_, i) => (
      <ListRowSkeleton key={i} />
    ))}
  </div>
);
